import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { works } from "@/mocks/works";
import { stars } from "@/mocks/stars";

interface SearchResult {
  key: string;
  title: string;
  subtitle: string;
  cover: string;
  path: string;
  kind: "作品" | "明星";
}

export default function SearchBox({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const workHits = works
      .filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.genre.some((g) => g.toLowerCase().includes(q)) ||
          item.director.toLowerCase().includes(q) ||
          item.cast.some((c) => c.toLowerCase().includes(q)),
      )
      .slice(0, 5)
      .map<SearchResult>((item) => ({
        key: `work-${item.id}`,
        title: item.title,
        subtitle: `${item.year} · ${item.type} · ${item.genre.join(" / ")}`,
        cover: item.cover,
        path: `/works/${item.id}`,
        kind: "作品",
      }));
    const starHits = stars
      .filter(
        (item) => item.name.toLowerCase().includes(q) || item.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 4)
      .map<SearchResult>((item) => ({
        key: `star-${item.id}`,
        title: item.name,
        subtitle: `演员 · ${item.active} · ${item.tags.join(" / ")}`,
        cover: item.avatar,
        path: `/stars/${item.id}`,
        kind: "明星",
      }));
    return [...workHits, ...starHits];
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <div ref={boxRef} className={`relative ${variant === "mobile" ? "w-full" : "w-56 lg:w-64"}`}>
      <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 focus-within:border-primary-400">
        <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results.length > 0) go(results[0].path);
          }}
          placeholder="搜索作品 / 明星"
          className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="清空搜索"
            className="flex h-4 w-4 items-center justify-center text-foreground-400 hover:text-foreground-700"
          >
            <i className="ri-close-line"></i>
          </button>
        )}
      </div>

      {open && query.trim() !== "" && (
        <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-[22rem] max-w-[88vw] overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg animate-fade-in">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-foreground-500">
              未找到与“{query}”相关的内容
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => go(item.path)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-background-100"
                  >
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="h-11 w-11 shrink-0 rounded-md object-cover object-top"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground-900">{item.title}</span>
                      <span className="block truncate text-xs text-foreground-500">{item.subtitle}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                      {item.kind}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-background-200 px-3 py-2 text-center text-xs text-foreground-400">
            搜索结果覆盖影视作品与明星资讯
          </div>
        </div>
      )}
    </div>
  );
}